/**
 * Shared fail-closed pick-list signal (WSRADM-0003; handoff §8.2).
 *
 * The distinct error every live reference-data loader raises when it cannot load
 * a list COMPLETELY — an upstream/network failure, or a `200` whose body is not
 * the expected array. It is intentionally NOT an empty array, so a failure can
 * never masquerade as "loaded, empty": `usePickList` (`@/hooks/usePickList`)
 * switches on {@link isPickListError} to keep `items` `null` and render
 * `error + Retry`.
 *
 * This module carries only the generic signal + guard. dink-admin couples a
 * domain `PickListKind` union and v2 endpoint wrappers here; those are domain
 * concerns (Non-Goal for this conventions unit). A future wsr-admin feature's
 * `api/<resource>.ts` wrapper constructs a `PickListError` with a short label for
 * the list it loads (e.g. `new PickListError('locations', cause)`).
 */

/**
 * The distinct fail-closed signal. Thrown by a pick-list wrapper when the live
 * list cannot be loaded completely. The loader uses {@link isPickListError} to
 * render `error + Retry` instead of an empty list.
 */
export class PickListError extends Error {
  /** A stable discriminant for `instanceof`-free checks (e.g. across realms). */
  readonly isPickListError = true;
  /** A short label for which pick-list failed — for the error copy / diagnostics. */
  readonly picklist: string;
  /** The originating error (an `ApiError`, a malformed-body marker, …), if any. */
  override readonly cause: unknown;

  constructor(picklist: string, cause?: unknown) {
    super(`Failed to load pick-list "${picklist}"`);
    this.name = 'PickListError';
    this.picklist = picklist;
    this.cause = cause;
  }
}

/**
 * True for a {@link PickListError} regardless of realm — prefer this over a bare
 * `instanceof` at consumer boundaries (the loader uses it to detect fail-closed).
 */
export function isPickListError(error: unknown): error is PickListError {
  return (
    error instanceof PickListError ||
    (typeof error === 'object' &&
      error !== null &&
      (error as { isPickListError?: unknown }).isPickListError === true)
  );
}
